<template>
  <div class="export-livret-container">
    <b-loading :is-full-page="isFullPage" v-model="isLoading"></b-loading>

    <div class="container mt-4 mb-4" v-if="!error">
      <div class="columns">
        <div class="column">
          <div class="is-flex align-items-center mb-4">
            <router-link
              :to="getBackUrl()"
              class="mr-3"
            >
              <b-icon icon="arrow-left" size="is-medium"></b-icon>
            </router-link>
            <h1 class="title is-4">Livret de suivi</h1>
          </div>

          <div class="buttons mb-4">
            <b-button
              type="is-primary"
              icon-left="printer"
              @click="printContent"
              v-if="content && !isLoading"
            >
              Imprimer
            </b-button>
            <b-button
              type="is-success"
              icon-left="download"
              style="display: none;"
              @click="exportToDocx"
              v-if="content && !isLoading"
            >
              Exporter en DOCX
            </b-button>
            <b-button
              type="is-info"
              icon-left="copy"
              @click="copyToClipboard"
              v-if="content && !isLoading"
            >
              Copier
            </b-button>
          </div>

          <div
            class="export-content"
            v-if="content"
            v-html="content"
          ></div>

          <div v-if="!content && !isLoading" class="has-text-centered">
            <p>Aucun contenu disponible.</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="container mt-4">
      <b-message type="is-danger" has-icon>
        <h4 class="title is-5">Erreur</h4>
        <p>{{ errorMessage }}</p>
        <div class="buttons mt-4">
          <b-button @click="retryLoad" type="is-primary">Réessayer</b-button>
          <b-button @click="goBack" type="is-light">Retour</b-button>
        </div>
      </b-message>
    </div>
  </div>
</template>

<script>
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  ImageRun,
} from 'docx';
import { saveAs } from 'file-saver';

export default {
  name: 'ExportLivret',
  data() {
    return {
      isLoading: false,
      isFullPage: true,
      content: '',
      error: false,
      errorMessage: '',
    };
  },
  computed: {
    idTraining() {
      return this.$route.params.idTraining;
    },
    student_id() {
      return this.$route.params.student_id;
    },
  },
  mounted() {
    this.loadContent();
  },
  methods: {
    loadContent() {
      this.isLoading = true;
      this.error = false;
      this.errorMessage = '';
      this.content = '';

      const url = `export_livret/?training_id=${this.idTraining}&student_id=${this.student_id}&myview=1&booklet=1&waypoints=1`;

      this.$http
        .get(url, {
          responseType: 'text',
          timeout: 60000, // 60 secondes de timeout pour cette requête
        })
        .then((response) => {
          if (!response.data || response.data.trim() === '') {
            this.error = true;
            this.errorMessage = "Aucun contenu disponible pour cet export.";
          } else {
            this.content = response.data;
          }
          this.isLoading = false;
        })
        .catch((error) => {
          console.error('Erreur lors du chargement du livret:', error);
          this.error = true;
          this.errorMessage = error.response
            ? `Erreur ${error.response.status}: ${error.response.statusText}`
            : 'Une erreur est survenue lors du chargement du livret. Veuillez réessayer.';
          this.isLoading = false;
        });
    },
    printContent() {
      // Imprimer directement la page actuelle
      window.print();
    },
    async copyToClipboard() {
      if (!this.content) {
        return;
      }

      try {
        // Créer un élément temporaire pour copier le HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.content;

        // Supprimer uniquement les scripts (garder les styles pour les traiter)
        tempDiv.querySelectorAll('script').forEach(el => el.remove());

        // Traiter d'abord les styles dans les balises <style> (avant de traiter les éléments)
        const styleTags = tempDiv.querySelectorAll('style');
        styleTags.forEach(styleTag => {
          let styleContent = styleTag.innerHTML;
          // Remplacer width:100% par width:170mm dans les CSS
          styleContent = styleContent.replace(/width\s*:\s*100%/gi, 'width:170mm');
          // Remplacer aussi width="100%" dans les attributs style
          styleContent = styleContent.replace(/width\s*=\s*["']100%["']/gi, 'width="170mm"');
          styleTag.innerHTML = styleContent;
        });

        // Convertir les largeurs 100% en 170mm dans tous les éléments
        const allElements = tempDiv.querySelectorAll('*');
        allElements.forEach(el => {
          // Traiter les attributs style inline
          if (el.hasAttribute('style')) {
            let style = el.getAttribute('style');
            // Remplacer width:100% par width:170mm (avec ou sans espaces)
            style = style.replace(/width\s*:\s*100%/gi, 'width:170mm');
            // Remplacer aussi les cas avec width="100%" dans le style
            style = style.replace(/width\s*=\s*["']100%["']/gi, 'width="170mm"');
            el.setAttribute('style', style);
          }

          // Traiter les attributs width directement
          if (el.hasAttribute('width')) {
            const width = el.getAttribute('width');
            // Convertir 100% ou "100%" ou '100%' en 170mm (enlever les guillemets si présents)
            const cleanWidth = width.replace(/["']/g, '');
            if (cleanWidth === '100%') {
              el.setAttribute('width', '170mm');
            }
          }
        });

        // Traitement final sur le HTML pour s'assurer que toutes les largeurs sont converties
        let htmlContent = tempDiv.innerHTML;
        // Remplacer width="100%" directement dans le HTML
        htmlContent = htmlContent.replace(/width\s*=\s*["']100%["']/gi, 'width="170mm"');
        // Remplacer width:100% dans les attributs style du HTML
        htmlContent = htmlContent.replace(/style\s*=\s*["']([^"']*width\s*:\s*)100%([^"']*)["']/gi, (match, before, after) => {
          return `style="${before}170mm${after}"`;
        });
        
        const textContent = tempDiv.textContent || tempDiv.innerText || '';

        // Méthode 1 : Utiliser l'API Clipboard moderne avec HTML et texte
        if (navigator.clipboard && window.ClipboardItem) {
          try {
            const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
            const textBlob = new Blob([textContent], { type: 'text/plain' });
            
            const clipboardItem = new ClipboardItem({
              'text/html': htmlBlob,
              'text/plain': textBlob,
            });
            
            await navigator.clipboard.write([clipboardItem]);

            this.$buefy.toast.open({
              message: 'Contenu HTML avec formatage copié dans le presse-papier !',
              type: 'is-success',
              duration: 3000,
            });
            return;
          } catch (err) {
            console.warn('ClipboardItem non supporté, utilisation de la méthode alternative', err);
          }
        }

        // Méthode 2 : Utiliser une div éditable avec execCommand (préserve le HTML)
        const editableDiv = document.createElement('div');
        editableDiv.contentEditable = true;
        editableDiv.innerHTML = htmlContent;
        editableDiv.style.position = 'fixed';
        editableDiv.style.left = '-9999px';
        editableDiv.style.top = '0';
        document.body.appendChild(editableDiv);

        // Sélectionner le contenu
        const range = document.createRange();
        range.selectNodeContents(editableDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        try {
          // Copier avec execCommand (préserve le formatage HTML)
          const successful = document.execCommand('copy');
          
          if (successful) {
            this.$buefy.toast.open({
              message: 'Contenu HTML avec formatage copié dans le presse-papier !',
              type: 'is-success',
              duration: 3000,
            });
          } else {
            throw new Error('execCommand copy a échoué');
          }
        } catch (err) {
          // Méthode 3 : Fallback avec texte brut
          const textArea = document.createElement('textarea');
          textArea.value = textContent;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.select();

          try {
            if (navigator.clipboard) {
              await navigator.clipboard.writeText(textContent);
            } else {
              document.execCommand('copy');
            }
            this.$buefy.toast.open({
              message: 'Contenu copié (texte uniquement).',
              type: 'is-warning',
              duration: 3000,
            });
          } catch (copyErr) {
            this.$buefy.toast.open({
              message: 'Erreur lors de la copie. Veuillez sélectionner le texte manuellement.',
              type: 'is-danger',
              duration: 4000,
            });
          } finally {
            document.body.removeChild(textArea);
          }
        } finally {
          selection.removeAllRanges();
          document.body.removeChild(editableDiv);
        }
      } catch (error) {
        console.error('Erreur lors de la copie:', error);
        this.$buefy.toast.open({
          message: 'Erreur lors de la copie dans le presse-papier.',
          type: 'is-danger',
          duration: 4000,
        });
      }
    },
    // Fonction helper pour charger une image depuis une URL
    async loadImageFromUrl(url) {
      try {
        // Si l'URL est relative, la convertir en absolue
        let imageUrl = url;
        if (url.startsWith('/')) {
          // Utiliser l'URL de base de l'API ou du site
          const baseUrl = process.env.VUE_APP_API_BASE_URL || window.location.origin;
          imageUrl = baseUrl + url;
        }

        const response = await fetch(imageUrl);
        if (!response.ok) {
          console.warn(`Impossible de charger l'image: ${imageUrl}`);
          return null;
        }

        const arrayBuffer = await response.arrayBuffer();
        const contentType = response.headers.get('content-type') || 'image/png';

        // Essayer de récupérer les dimensions réelles de l'image
        let width = 400;
        let height = 300;

        try {
          const img = new Image();
          const blob = new Blob([arrayBuffer], { type: contentType });
          const objectUrl = URL.createObjectURL(blob);
          
          await new Promise((resolve, reject) => {
            img.onload = () => {
              width = img.naturalWidth;
              height = img.naturalHeight;
              URL.revokeObjectURL(objectUrl);
              resolve();
            };
            img.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              reject();
            };
            img.src = objectUrl;
          });
        } catch (error) {
          // Utiliser les dimensions par défaut si on ne peut pas les récupérer
          console.warn('Impossible de récupérer les dimensions de l\'image, utilisation des valeurs par défaut');
        }

        return {
          data: arrayBuffer,
          width: Math.min(width, 600), // Limiter la largeur max
          height: Math.min(height, 450), // Limiter la hauteur max
          type: contentType.includes('png') ? 'png' : 'jpeg',
        };
      } catch (error) {
        console.error(`Erreur lors du chargement de l'image ${url}:`, error);
        return null;
      }
    },
    // Fonction helper pour convertir les éléments HTML en paragraphes DOCX
    async htmlToDocxElements(htmlContent) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;

      // Supprimer les scripts et styles
      tempDiv.querySelectorAll('script, style').forEach(el => el.remove());

      const elements = [];

      // Fonction récursive pour parcourir les nœuds
      const processNode = async (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            return [new TextRun(text)];
          }
          return [];
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();

          // Traiter les titres
          if (tagName.match(/^h[1-6]$/)) {
            const level = parseInt(tagName.charAt(1));
            const text = node.textContent.trim();
            if (text) {
              const headingLevels = {
                1: HeadingLevel.HEADING_1,
                2: HeadingLevel.HEADING_2,
                3: HeadingLevel.HEADING_3,
                4: HeadingLevel.HEADING_4,
                5: HeadingLevel.HEADING_5,
                6: HeadingLevel.HEADING_6,
              };
              elements.push(new Paragraph({
                text: text,
                heading: headingLevels[level] || HeadingLevel.HEADING_1,
                spacing: { after: 200 },
              }));
            }
            return [];
          }

          // Traiter les paragraphes
          if (tagName === 'p') {
            const textRuns = [];
            const images = [];

            for (const child of Array.from(node.childNodes)) {
              const result = await processNode(child);
              if (Array.isArray(result)) {
                textRuns.push(...result);
              } else if (result && result.type === 'image') {
                images.push(result);
              }
            }

            // Ajouter le paragraphe avec texte
            if (textRuns.length > 0) {
              elements.push(new Paragraph({
                children: textRuns,
                spacing: { after: 100 },
              }));
            }

            // Ajouter les images séparément
            for (const imgMarker of images) {
              const imageData = await this.loadImageFromUrl(imgMarker.src);
              if (imageData) {
                // Utiliser les dimensions de l'attribut HTML si disponibles, sinon celles détectées
                let imgWidth = imgMarker.width || imageData.width;
                let imgHeight = imgMarker.height || imageData.height;

                // Convertir ArrayBuffer en Uint8Array pour docx
                const uint8Array = new Uint8Array(imageData.data);
                elements.push(new Paragraph({
                  children: [
                    new ImageRun({
                      data: uint8Array,
                      transformation: {
                        width: Math.min(imgWidth, 500),
                        height: Math.min(imgHeight, 400),
                      },
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 200 },
                }));
              }
            }

            return [];
          }

          // Traiter les listes
          if (tagName === 'ul' || tagName === 'ol') {
            for (const li of Array.from(node.querySelectorAll('li'))) {
              const text = li.textContent.trim();
              if (text) {
                elements.push(new Paragraph({
                  text: `• ${text}`,
                  spacing: { after: 50 },
                }));
              }
            }
            return [];
          }

          // Traiter les images
          if (tagName === 'img') {
            const src = node.getAttribute('src');
            const width = node.getAttribute('width');
            const height = node.getAttribute('height');
            if (src) {
              // Retourner un marqueur pour traiter les images plus tard avec les dimensions
              return {
                type: 'image',
                src,
                width: width ? parseInt(width) : null,
                height: height ? parseInt(height) : null,
              };
            }
            return [];
          }

          // Traiter les tableaux
          if (tagName === 'table') {
            const rows = node.querySelectorAll('tr');
            const tableRows = [];

            for (const row of rows) {
              const cells = row.querySelectorAll('td, th');
              const tableCells = [];

              for (const cell of cells) {
                // Traiter le contenu de chaque cellule
                const cellContent = [];
                const cellParagraphs = [];

                // Parcourir les nœuds de la cellule
                const processCellNode = async (cellNode) => {
                  if (cellNode.nodeType === Node.TEXT_NODE) {
                    const text = cellNode.textContent.trim();
                    if (text) {
                      return [new TextRun(text)];
                    }
                    return [];
                  }

                  if (cellNode.nodeType === Node.ELEMENT_NODE) {
                    const cellTagName = cellNode.tagName.toLowerCase();

                    if (cellTagName === 'img') {
                      const src = cellNode.getAttribute('src');
                      const width = cellNode.getAttribute('width');
                      const height = cellNode.getAttribute('height');
                      if (src) {
                        const imageData = await this.loadImageFromUrl(src);
                        if (imageData) {
                          return {
                            type: 'image',
                            data: imageData,
                            width: width ? parseInt(width) : null,
                            height: height ? parseInt(height) : null,
                          };
                        }
                      }
                      return [];
                    }

                    if (cellTagName === 'strong' || cellTagName === 'b') {
                      return [new TextRun({ text: cellNode.textContent.trim(), bold: true })];
                    }

                    if (cellTagName === 'em' || cellTagName === 'i') {
                      return [new TextRun({ text: cellNode.textContent.trim(), italics: true })];
                    }

                    // Traiter récursivement les autres éléments
                    const textRuns = [];
                    for (const child of Array.from(cellNode.childNodes)) {
                      const runs = await processCellNode(child);
                      if (Array.isArray(runs)) {
                        textRuns.push(...runs);
                      }
                    }
                    return textRuns;
                  }

                  return [];
                };

                for (const child of Array.from(cell.childNodes)) {
                  const content = await processCellNode(child);
                  if (Array.isArray(content)) {
                    cellContent.push(...content);
                  } else if (content && content.type === 'image') {
                    // Gérer les images dans les cellules
                    cellContent.push(content);
                  }
                }

                // Séparer les TextRuns et les images
                const textRuns = cellContent.filter(c => c instanceof TextRun);
                const images = cellContent.filter(c => c && c.type === 'image');

                const cellChildren = [];

                // Ajouter le paragraphe avec texte si présent
                if (textRuns.length > 0) {
                  cellChildren.push(new Paragraph({
                    children: textRuns,
                  }));
                }

                // Ajouter les images si présentes
                for (const imgMarker of images) {
                  if (imgMarker.data) {
                    const uint8Array = new Uint8Array(imgMarker.data.data);
                    // Utiliser les dimensions de l'attribut HTML si disponibles, sinon celles détectées
                    let imgWidth = imgMarker.width || imgMarker.data.width;
                    let imgHeight = imgMarker.height || imgMarker.data.height;

                    cellChildren.push(new Paragraph({
                      children: [
                        new ImageRun({
                          data: uint8Array,
                          transformation: {
                            width: Math.min(imgWidth, 300),
                            height: Math.min(imgHeight, 250),
                          },
                        }),
                      ],
                      alignment: AlignmentType.CENTER,
                    }));
                  }
                }

                // Si pas de contenu, créer un paragraphe vide
                if (cellChildren.length === 0) {
                  cellChildren.push(new Paragraph({ text: '' }));
                }

                tableCells.push(new TableCell({
                  children: cellChildren,
                }));
              }

              if (tableCells.length > 0) {
                tableRows.push(new TableRow({
                  children: tableCells,
                }));
              }
            }

            if (tableRows.length > 0) {
              elements.push(new Table({
                rows: tableRows,
                width: {
                  size: 100,
                  type: WidthType.PERCENTAGE,
                },
              }));
              elements.push(new Paragraph({ text: '', spacing: { after: 200 } })); // Espace après le tableau
            }
            return [];
          }

          // Traiter les éléments inline (strong, em, etc.)
          if (tagName === 'strong' || tagName === 'b') {
            const text = node.textContent.trim();
            if (text) {
              return [new TextRun({ text, bold: true })];
            }
          }

          if (tagName === 'em' || tagName === 'i') {
            const text = node.textContent.trim();
            if (text) {
              return [new TextRun({ text, italics: true })];
            }
          }

          // Pour les autres éléments, traiter récursivement
          const textRuns = [];
          for (const child of Array.from(node.childNodes)) {
            const runs = await processNode(child);
            if (Array.isArray(runs)) {
              textRuns.push(...runs);
            }
          }
          return textRuns;
        }

        return [];
      };

      // Traiter tous les nœuds de manière asynchrone
      for (const child of Array.from(tempDiv.childNodes)) {
        await processNode(child);
      }

      // Traiter les images qui sont en dehors des paragraphes
      const allImages = tempDiv.querySelectorAll('img');
      for (const img of allImages) {
        // Vérifier si l'image n'a pas déjà été traitée
        const src = img.getAttribute('src');
        if (src && !tempDiv.querySelector(`p img[src="${src}"]`)) {
          const imageData = await this.loadImageFromUrl(src);
          if (imageData) {
            const uint8Array = new Uint8Array(imageData.data);
            elements.push(new Paragraph({
              children: [
                new ImageRun({
                  data: uint8Array,
                  transformation: {
                    width: Math.min(imageData.width, 500),
                    height: Math.min(imageData.height, 400),
                  },
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 },
            }));
          }
        }
      }

      // Si aucun élément spécifique n'a été trouvé, créer un paragraphe simple
      if (elements.length === 0) {
        const text = tempDiv.textContent.trim();
        if (text) {
          // Diviser le texte en paragraphes
          text.split(/\n+/).forEach(line => {
            const trimmed = line.trim();
            if (trimmed) {
              elements.push(new Paragraph({
                text: trimmed,
                spacing: { after: 100 },
              }));
            }
          });
        }
      }

      return elements;
    },
    async exportToDocx() {
      if (!this.content) {
        return;
      }

      try {
        this.isLoading = true;

        // Convertir le HTML en éléments DOCX (async)
        const docxElements = await this.htmlToDocxElements(this.content);

        // Si aucun élément, créer un paragraphe vide
        if (docxElements.length === 0) {
          docxElements.push(new Paragraph({
            text: 'Aucun contenu disponible',
          }));
        }

        // Créer le document DOCX
        const doc = new Document({
          sections: [{
            properties: {},
            children: [
              new Paragraph({
                text: 'Livret de suivi',
                heading: HeadingLevel.HEADING_1,
                spacing: { after: 400 },
              }),
              ...docxElements,
            ],
          }],
        });

        // Générer le blob et télécharger
        const blob = await Packer.toBlob(doc);

        // Générer un nom de fichier
        const studentId = this.student_id || 'livret';
        const trainingId = this.idTraining || 'formation';
        const fileName = `livret_suivi_${trainingId}_${studentId}_${new Date().toISOString().split('T')[0]}.docx`;

        // Télécharger avec file-saver
        saveAs(blob, fileName);

        this.$buefy.toast.open({
          message: 'Export DOCX réussi !',
          type: 'is-success',
          duration: 3000,
        });
      } catch (error) {
        console.error('Erreur lors de l\'export DOCX:', error);
        this.$buefy.toast.open({
          message: 'Erreur lors de l\'export DOCX. Veuillez réessayer.',
          type: 'is-danger',
          duration: 4000,
        });
      } finally {
        this.isLoading = false;
      }
    },
    retryLoad() {
      this.loadContent();
    },
    goBack() {
      this.$router.go(-1);
    },
    getBackUrl() {
      // Essayer de construire une URL de retour basée sur les paramètres disponibles
      if (this.$route.params.idStudentTraining && this.$route.params.idStudent) {
        return `/formation-apprenant/${this.idTraining}/${this.$route.params.idStudent}/${this.$route.params.idStudentTraining}/waypoint`;
      }
      // Sinon, retourner à la page précédente ou à la formation
      if (this.$route.params.idTraining) {
        return `/formations/${this.idTraining}`;
      }
      return '/';
    },
  },
  watch: {
    // Recharger si les paramètres de route changent
    '$route'(to, from) {
      if (to.params.idTraining !== from.params.idTraining ||
          to.params.student_id !== from.params.student_id) {
        this.loadContent();
      }
    },
  },
};
</script>

<style scoped>
.export-livret-container {
  width:900px;
  padding:20px;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.export-content {
  background-color: #fff;
  padding: 2rem;
  border-radius: 4px;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
}

/* Styles pour le contenu HTML importé */
.export-content >>> * {
  max-width: 100%;
}

.export-content >>> img {
  max-width: 100%;
  height: auto;
}

.export-content >>> table {
  width: 100%;
  border-collapse: collapse;
}

@media print {
  .export-livret-container {
    background-color: #fff;
  }

  .buttons,
  .is-flex.align-items-center {
    display: none !important;
  }

  .export-content {
    box-shadow: none;
    padding: 0;
  }

  @page {
    margin: 1cm;
  }

  body {
    background-color: #fff;
  }
}
</style>
